// Экран «Модель» — шапка и три вида (п.20, 2026-09-07). Последняя часть переезда:
// раньше шапка (виды, галочки столбцов, «Актуализировать», базовый план и дата
// последней актуализации) жила в оболочке, а таблицу/Гант/Сводную она встраивала
// по одному. Теперь экран самодостаточный: шапка здесь, виды — дочерние компоненты.
import { useCallback, useEffect, useState } from 'react';
import { ask } from '../dialog';
import { call, p, type Data } from '../api';
import type { ShellCtx } from '../shell';
import { Plan, type SvodkaOshibok } from './Plan';

type Baseline = Data<'/api/projects/{pid}/baselines', 'get'>[number];
type Project = Data<'/api/projects', 'get'>[number];
type Recalc = Data<'/api/projects/{pid}/recalc', 'post'>;
type UndoState = Data<'/api/projects/{pid}/undo/state', 'get'>;
type UndoRes = Data<'/api/projects/{pid}/undo', 'post'>;
type View = 'table' | 'gant' | 'pivot' | 'shahm';
type Cols = { hand: boolean; res: boolean; pred: boolean; fakt: boolean; sprav: boolean;
              base: boolean; noSum: boolean };

// значки видов и панели снизу (владелец 2026-09-15, #86): справа над диаграммой
const IkGant = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <rect x="1" y="2.5" width="8" height="2.6" rx="1" fill="currentColor" />
    <rect x="4" y="6.7" width="9" height="2.6" rx="1" fill="currentColor" />
    <rect x="7" y="10.9" width="8" height="2.6" rx="1" fill="currentColor" />
  </svg>
);
const IkSvod = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <rect x="1.5" y="1.5" width="13" height="13" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <path d="M1.5 6h13M1.5 10.5h13M6 1.5v13M10.5 1.5v13" stroke="currentColor" strokeWidth="1.1" />
  </svg>
);
// «Скрыть суммарные» — перечёркнутый глаз всегда, нажатое состояние — подсветка кнопки (2026-09-15)
const IkGlaz = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M1.5 8s2.4-4.5 6.5-4.5S14.5 8 14.5 8s-2.4 4.5-6.5 4.5S1.5 8 1.5 8z" fill="none"
      stroke="currentColor" strokeWidth="1.3" />
    <circle cx="8" cy="8" r="2" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <path d="M2.5 13.5l11-11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
// поэтажная шахматка (2026-09-15): здание из клеток-этажей
const IkShahm = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <rect x="1.5" y="1.5" width="4" height="3.5" fill="currentColor" />
    <rect x="10.5" y="1.5" width="4" height="3.5" fill="currentColor" />
    <rect x="6" y="6.2" width="4" height="3.5" fill="currentColor" />
    <rect x="1.5" y="11" width="4" height="3.5" fill="currentColor" />
    <rect x="10.5" y="11" width="4" height="3.5" fill="currentColor" />
    <rect x="1.5" y="1.5" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1" />
  </svg>
);

const COLS_DEF: Cols = { hand: true, res: false, pred: false, fakt: false, sprav: false,
                         base: false, noSum: false };
/** ключи в localStorage те же, что были у оболочки: настройки пользователя не сбрасываются */
function loadCols(): Cols {
  try {
    const raw = JSON.parse(localStorage.getItem('plot_plancols') || '{}') as
      { h?: boolean; r?: boolean; pr?: boolean; f?: boolean; s?: boolean; b?: boolean; ns?: boolean };
    return { hand: raw.h === undefined ? true : !!raw.h, res: !!raw.r, pred: !!raw.pr, fakt: !!raw.f,
      sprav: false, base: !!raw.b, noSum: !!raw.ns };
  } catch { return COLS_DEF; }
}
function saveCols(c: Cols) {
  try {
    localStorage.setItem('plot_plancols',
      JSON.stringify({ h: c.hand, r: c.res, pr: c.pred, f: c.fakt, s: false, b: c.base, ns: c.noSum }));
  } catch { /* приватный режим */ }
}
function loadView(): View {
  try { return (localStorage.getItem('plot_planview') as View) || 'table'; } catch { return 'table'; }
}

export function Model({ ctx, ruch = false }: { ctx: ShellCtx; ruch?: boolean }) {
  // режим ручного ввода (решение владельца 2026-09-08): вместо отдельного экрана —
  // кнопка в шапке модели: строка ввода внизу и только задачи ручных работ
  const [ruchMode, setRuchMode] = useState(() => {
    if (ruch) return true;
    try { return localStorage.getItem('plot_ruchmode') === '1'; } catch { return false; }
  });
  // корректировка суммарной задачи на Ганте (запрос владельца 2026-09-08)
  const setRuchSaved = (v: boolean) => {
    setRuchMode(v);
    try { localStorage.setItem('plot_ruchmode', v ? '1' : '0'); } catch { /* приватный режим */ }
  };
  const [view, setView] = useState<View>(loadView);
  const [cols, setCols] = useState<Cols>(loadCols);
  const [bl, setBl] = useState<Baseline[]>([]);
  const [blCur, setBlCur] = useState<string>('');
  const [aktual, setAktual] = useState<string | null>(null);
  // «Расчётный» — показать сроки по одной модели, будто рамок нет (2026-09-09)
  const [raschet, setRaschet] = useState(false);
  // «Ошибки» (владелец 2026-09-16: экран «Проверки» непонятен — нужна кнопка прямо в модели):
  // таблица оставляет только задачи с замечаниями; не запоминается, чтобы не прятать задачи
  const [oshibki, setOshibki] = useState(false);
  const [svodkaOshibok, setSvodkaOshibok] = useState<SvodkaOshibok>({ vsego: 0, vidy: [] });
  const [vidOshibki, setVidOshibki] = useState('');
  const chisloOshibok = svodkaOshibok.vsego;
  // выбранный вид пропал (исправили или сменили корпус) — показываем все замечания
  useEffect(() => {
    if (vidOshibki && !svodkaOshibok.vidy.some(v => v.chto === vidOshibki)) setVidOshibki('');
  }, [svodkaOshibok, vidOshibki]);
  // Гант на холсте (#100, владелец 2026-09-18: «переезжаем на новый гант»): значок «Гант»
  // открывает его всем, отдельной кнопки-переключателя больше нет. Аварийный выключатель
  // остался на сервере: GANT_CANVAS=0 в env службы → /api/ping отдаёт gant_canvas: false,
  // и тогда рисуется прежний Гант (полосы в ячейках таблицы).
  const [holstRazreshen, setHolstRazreshen] = useState(false);
  useEffect(() => {
    let zhiv = true;
    void call<Data<'/api/ping', 'get'>>('get', '/api/ping').then(r => {
      if (zhiv && r.ok) setHolstRazreshen(!!r.data.gant_canvas);
    });
    return () => { zhiv = false; };
  }, []);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(0);   // перечитать вид после пересчёта
  const [undo, setUndo] = useState<UndoState | null>(null);   // отмена действий (2026-09-07)
  const can = ctx.canPlan;
  const fail = (m: string) => ctx.onError?.(m);

  // старт проекта переехал на экран «Общее» (владелец 2026-09-15, #96)

  /** что сейчас можно отменить и повторить — подписи на стрелках */
  const loadUndo = useCallback(async () => {
    if (!ctx.canPlan) return;
    const r = await call<UndoState>('get', p('/api/projects/{pid}/undo/state', { pid: ctx.pid }));
    if (r.ok) setUndo(r.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.canPlan]);

  const loadHead = useCallback(async () => {
    void loadUndo();
    const [pr, b] = await Promise.all([
      call<Project[]>('get', '/api/projects'),
      call<Baseline[]>('get', p('/api/projects/{pid}/baselines', { pid: ctx.pid })),
    ]);
    if (pr.ok) {
      const cur = pr.data.find(x => x.id === ctx.pid);
      setAktual(cur?.aktual ?? null);
    }
    if (b.ok) {
      setBl(b.data);
      const act = b.data.find(x => x.active);
      setBlCur(act ? String(act.id) : (b.data[0] ? String(b.data[0].id) : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.reloadToken, loadUndo]);
  useEffect(() => { void loadHead(); }, [loadHead]);

  function setViewSaved(v: View) {
    setView(v);
    try { localStorage.setItem('plot_planview', v); } catch { /* приватный режим */ }
  }
  function setCol(k: keyof Cols, v: boolean) {
    const next = { ...cols, [k]: v };
    setCols(next);
    saveCols(next);
  }

  async function recalc() {
    setBusy(true);
    const r = await call<Recalc>('post', p('/api/projects/{pid}/recalc', { pid: ctx.pid }), {});
    setBusy(false);
    if (!r.ok) { fail(r.error.message); return; }
    ctx.onError?.('');
    setAktual(r.data.aktualizirovano);
    const total = (r.data.korpusa || []).reduce((s, k) => s + (k.zadach || 0), 0);
    // круг в задачах (п.34, 2026-09-10): движок вернул −1 — даты посчитаны
    // страховочным способом, планеру надо об этом сказать, а не молчать
    const krug = (r.data.korpusa || []).filter(k => (k.iteraciy ?? 0) < 0).map(k => k.korpus);
    if (krug.length) {
      fail('Задачи ждут друг друга по кругу (' + krug.join(', ') + ') — даты посчитаны'
        + ' страховочным способом. Разбор — на экране «Проверки», строка «Расчёт зациклился».');
    } else {
      ctx.onSaved?.('Актуализация готова: ' + total + ' задач за ' + r.data.sekund + ' с. Даты обновлены.');
    }
    ctx.onAfterRecalc?.(r.data.aktualizirovano);
    setToken(t => t + 1);
  }

  /** отменить или вернуть последнее своё действие (только «Модель ГПР») */
  async function shag(napravlenie: 'undo' | 'redo') {
    setBusy(true);
    const r = await call<UndoRes>('post',
      p('/api/projects/{pid}/' + napravlenie, { pid: ctx.pid }), {});
    setBusy(false);
    if (!r.ok) { fail(r.error.message); return; }
    ctx.onError?.('');
    setUndo(r.data);
    ctx.onSaved?.((napravlenie === 'undo' ? 'Отменено: ' : 'Возвращено: ')
      + (r.data.sdelano || '') + ' (строк: ' + r.data.zadach
      + '). Нажмите «Актуализировать», чтобы пересчитать даты.');
    setToken(t => t + 1);
  }

  // кнопка «Связи» (стрелки связей на Ганте, 2026-09-10) убрана владельцем 2026-09-15 (#90)

  // значок «Панель снизу» убран владельцем 2026-09-16: карточка задачи открывается нижней
  // панелью сама при клике по задаче (память plot_detali_vnizu больше не читается)

  /** значение пункта-действия в списке базовых планов */
  const NOVYJ_BAZOVYJ = 'novyj';

  async function setBaseline() {
    const d = new Date(), p2 = (n: number) => String(n).padStart(2, '0');
    const def = 'План от ' + p2(d.getDate()) + '.' + p2(d.getMonth() + 1) + '.' + d.getFullYear();
    const name = await ask('Название новой версии базового плана (снимок текущих расчётных дат):', def);
    // список сразу возвращаем к действующей версии: пункт «＋ Задать…» — действие,
    // а не выбор, и он не должен оставаться выбранным (2026-09-10)
    setBlCur(String(bl.find(b => b.active)?.id ?? ''));
    if (name === null) return;
    const r = await call<{ id: number; zadach: number }>('post',
      p('/api/projects/{pid}/baselines', { pid: ctx.pid }), { name: name.trim() || def });
    if (!r.ok) { fail(r.error.message); return; }
    ctx.onError?.('');
    ctx.onSaved?.('Базовый план «' + (name.trim() || def) + '» задан: ' + r.data.zadach
      + ' задач. Он стал действующим.');
    await loadHead();
    setToken(t => t + 1);
  }

  async function pickBaseline(id: string) {
    setBlCur(id);
    if (!id) return;
    const r = await call('patch', p('/api/projects/{pid}/baselines/{bid}', { pid: ctx.pid, bid: id }),
      { active: true });
    if (!r.ok) { fail(r.error.message); return; }
    ctx.onError?.('');
    setBl(prev => prev.map(b => ({ ...b, active: String(b.id) === id })));
    setToken(t => t + 1);
  }

  // дата последнего пересчёта — коротко, чтобы полоса оставалась в одну строку
  const actShort = (() => {
    if (!aktual) return 'не актуализировалось';
    const d = new Date(aktual), p2 = (n: number) => String(n).padStart(2, '0');
    return p2(d.getDate()) + '.' + p2(d.getMonth() + 1) + ' ' + p2(d.getHours()) + ':' + p2(d.getMinutes());
  })();

  // виду передаём тот же контекст плюс выбранные столбцы и свой счётчик обновления
  const childCtx: ShellCtx = {
    ...ctx, planCols: cols, reloadToken: (ctx.reloadToken || 0) * 1000 + token,
    // правка в таблице или Ганте прошла — стрелка «отменить» должна ожить
    onSaved: (m?: string) => { ctx.onSaved?.(m); void loadUndo(); },
  };

  return (
    <div>
      <div className="bar plan-bar actbar">
        {can && (
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', marginRight: 6 }}>
            <button className="sec und" disabled={busy || !undo?.mozhno_otmenit}
              title={undo?.mozhno_otmenit
                ? 'Отменить последнее своё действие: ' + (undo.opisanie || '')
                : 'Отменять нечего (отменяются только свои правки в модели, до 10 шагов)'}
              onClick={() => void shag('undo')}>↺</button>
            <button className="sec und" disabled={busy || !undo?.mozhno_povtorit}
              title={undo?.mozhno_povtorit
                ? 'Вернуть отменённое: ' + (undo.opisanie_povtora || '')
                : 'Возвращать нечего'}
              onClick={() => void shag('redo')}>↻</button>
          </span>
        )}
        {/* «Скрыть суммарные» — рядом со стрелками отмены (владелец 2026-09-15) */}
        <button className={'sec icobtn pv-ico' + (cols.noSum ? ' on' : '')} disabled={ruchMode}
          aria-label="Скрыть суммарные"
          title={ruchMode ? 'В «Ручном вводе» суммарных строк нет — список задач' :
            (cols.noSum ? 'Суммарные скрыты — показать' : 'Скрыть')
            + ' жирные строки-итоги по работам и этапам (сортировка по столбцу действует внутри работы)'}
          onClick={() => setCol('noSum', !cols.noSum)}><IkGlaz /></button>

        {/* всё в одну строку: значок пересчёта и дата рядом (замечание владельца 2026-09-08) */}
        {/* всё выровнено по левому краю (просьба владельца 2026-09-10):
            раньше группа прижималась вправо и на широком экране уезжала от глаз */}
        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center',
          flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          {/* виды — отдельной группой, рядом с пересчётом (владелец 2026-09-08);
              «Скрыть суммарные» тоже значком, чтобы шапка была в одну строку */}
          {/* Таблица есть всегда (решение владельца 2026-09-10) — отдельной
              кнопки «Таблица» нет. Эти кнопки говорят, ЧТО показать в правой
              части: столбцы модели или Гант/Сводную. Клик по столбцу при
              включённом Ганте возвращает столбцы. */}
          {(
            <span className="pv-stolbcy">
              {([['hand', 'Сроки', 'Ручные длительность, сдвиг начала и даты — правка расчёта'],
                 ['res', 'Ресурс', 'Объём работ и ручной приоритет задачи в очереди бригады'],
                 ['pred', 'Предшественники', 'Предшественники и даты-источники расчёта: по связи, по ресурсу (бригаде)'],
                 ['fakt', 'Факт', 'Подрядчик и фактические начало и окончание (из шахматки факта)'],
                 ['base', 'Базовый', 'Базовое начало и окончание из выбранной версии базового плана']] as [keyof Cols, string, string][])
                .map(([k, t, ttl]) => (
                  <span key={k} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <button className={'sec pv-btn' + (cols[k] ? ' on' : '')}
                      title={ttl + '. Клик — показать или скрыть столбцы; Гант и сводная встают правее (#47)'}
                      onClick={() => setCol(k, !cols[k])}>{t}</button>
                    {/* версия базового плана — сразу за своей кнопкой; там же
                        пункт «Задать базовый план…» (2026-09-10) */}
                    {k === 'base' && cols.base && (
                      <select value={blCur} style={{ maxWidth: 240 }} disabled={!bl.length && !can}
                        title="Какая версия базового плана считается действующей; там же — задать новую"
                        onChange={e => {
                          if (e.target.value === NOVYJ_BAZOVYJ) { void setBaseline(); return; }
                          void pickBaseline(e.target.value);
                        }}>
                        {!bl.length && <option value="">— не задан —</option>}
                        {bl.map(b => <option key={b.id} value={b.id}>{b.name + ' (' + b.sozdan.slice(0, 10) + ')'}</option>)}
                        {can && <option value={NOVYJ_BAZOVYJ}>＋ Задать базовый план…</option>}
                      </select>
                    )}
                  </span>
                ))}
            </span>
          )}
          <button className={'sec pv-btn' + (ruchMode ? ' on' : '')}
            title={ruchMode
              ? 'Выключить ручной ввод: вернуть в таблицу все задачи модели'
              : 'Ручной ввод: скрыть расчётные работы, оставить внесённые вручную и строку ввода внизу'}
            onClick={() => setRuchSaved(!ruchMode)}>Ручной ввод</button>
          <button className={'sec pv-btn' + (raschet ? ' on' : '')}
            title={raschet
              ? 'Вернуться к плану: даты с учётом рамок суммарных задач'
              : 'Расчётный график: сроки по одной модели — связи, очередь бригад, ручные даты '
                + 'и факт — без декоративных сроков (рамок и декоративных длительностей от корректировки '
                + 'суммарной). Только показ, ничего не меняется'}
            onClick={() => setRaschet(!raschet)}>Расчётный</button>
          <button className={'sec pv-btn' + (oshibki ? ' on' : '')}
            style={!oshibki && chisloOshibok ? { color: 'var(--plot-danger)' } : undefined}
            title={oshibki
              ? 'Вернуть в таблицу все задачи'
              : chisloOshibok
                ? 'Показать только задачи с замечаниями (' + chisloOshibok + ' в выбранных корпусах): ошибки — '
                  + 'розовые, конфликты — песочные; что не так — в подсказке строки'
                : 'В выбранных корпусах задач с замечаниями нет'}
            onClick={() => setOshibki(!oshibki)}>Ошибки{chisloOshibok ? ' ' + chisloOshibok : ''}</button>
          {oshibki && svodkaOshibok.vidy.length > 0 && (
            <select value={vidOshibki} style={{ width: 200, padding: '4px 6px', fontSize: 13 }}
              title="Какой вид замечаний показать. Ошибка — данные неверны, конфликт — сроки противоречат друг другу"
              onChange={e => setVidOshibki(e.target.value)}>
              <option value="">Все замечания — {chisloOshibok}</option>
              {svodkaOshibok.vidy.map(v => (
                <option key={v.chto} value={v.chto}>{(v.gruppa === 'ошибка' ? '● ' : '○ ') + v.chto + ' — ' + v.n}</option>
              ))}
            </select>
          )}
        </span>
        {/* виды и панель снизу — тремя значками справа в этой же строке (владелец 2026-09-15:
            «значки визуализации перенеси в ту же строку, где кнопки»); вторая строка шапки
            убрана — в ней после #86/#90 ничего не осталось */}
        <span className="pv-ikonki">
          {/* «Пересчитать» с датой — справа, сразу слева от значков видов (владелец 2026-09-15);
              старт проекта — на экране «Общее» (#96) */}
          <span className="muted" title="Когда даты проекта пересчитывались в последний раз">{actShort}</span>
          {can && <button disabled={busy} style={{ marginRight: 6 }}
            title={busy ? 'Считаю…' : 'Пересчитать даты всех задач проекта (актуализация)'}
            onClick={() => void recalc()}>{busy ? 'Считаю…' : 'Пересчитать'}</button>}
          <button className={'sec icobtn pv-ico' + (view === 'gant' ? ' on' : '')} aria-label="Гант"
            title={(view === 'gant' ? 'Убрать диаграмму Ганта' : 'Диаграмма Ганта: полосы задач по календарю')
              + ' — правее открытых блоков столбцов. В Ганте тянутся полосы: суммарная целиком — '
              + 'группа начнётся не раньше новой даты, за правый край — длительности; полоса задачи — '
              + 'ручные сроки (отмена — стрелкой «назад»); двойной клик по суммарной — сбросить корректировки'}
            onClick={() => setViewSaved(view === 'gant' ? 'table' : 'gant')}><IkGant /></button>
          <button className={'sec icobtn pv-ico' + (view === 'pivot' ? ' on' : '')} aria-label="Сводная"
            title={view === 'pivot' ? 'Убрать сводную' : 'Сводная по месяцам: сколько задач заканчивается'}
            onClick={() => setViewSaved(view === 'pivot' ? 'table' : 'pivot')}><IkSvod /></button>
          <button className={'sec icobtn pv-ico' + (view === 'shahm' ? ' on' : '')} aria-label="Шахматка"
            title={view === 'shahm' ? 'Вернуть таблицу задач'
              : 'Поэтажная шахматка: по корпусам этажи × работы, в клетке — дата окончания. '
                + 'Цвет: закончено, начато, срок прошёл. Клик по клетке — сведения о работе в панели снизу'}
            onClick={() => setViewSaved(view === 'shahm' ? 'table' : 'shahm')}><IkShahm /></button>
        </span>
      </div>

      {/* один экран: слева всегда те же колонки, справа — выбранная визуализация
          (решение владельца 2026-09-08) */}
      <Plan ctx={childCtx} vid={view} ruch={ruchMode} summ={view === 'gant'} raschet={raschet}
        oshibki={oshibki} vidOshibki={vidOshibki} onOshibki={setSvodkaOshibok}
        holst={holstRazreshen} />
    </div>
  );
}
